import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HTTP_INTERCEPTORS, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
// Plugins
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { SessionStorage } from 'ngx-webstorage';
// Models
import { AuthData, RefreshRo } from '@models/auth/auth.model.ro';
// Shared
import { ENDPOINTS } from '@shared/endpoints.enum';
import { basicAuthConfig } from '@shared/config';
// Services
import { AuthService } from '@services/auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    @SessionStorage() public authData: AuthData;

    private isRefreshing = false;
    private refreshTokenSubject = new BehaviorSubject<string>(null);

    public constructor(
        private authService: AuthService,
    ) { }

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        let authRequest = request;
        let token: string = this.authData?.access_token;
        let authType: string = 'Bearer';

		authRequest = this.setRequiredHeaders(authRequest);

        if (authRequest.url?.search(`/${ ENDPOINTS.LOGIN }`) != -1) {
            token = btoa(basicAuthConfig.username + ':' + basicAuthConfig.password);
            authType = 'Basic';
        }

        if (authRequest.url?.search(ENDPOINTS.REFRESH) != -1) token = this.authData.refresh_token;

        if (token) authRequest = this.addTokenHeader(authRequest, token, authType);

        return next.handle(authRequest).pipe(catchError((error: HttpErrorResponse) => { 
            if (error instanceof HttpErrorResponse && !this.isAuthUrl(authRequest.url) && error.status === 401) {
                return this.handle401Error(authRequest, next);
            }

            return throwError(error);
        }));
    }

    private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            if (this.authData.refresh_token) {
                return this.authService.refresh({}).pipe(
                    switchMap((token: RefreshRo) => {
                        this.isRefreshing = false;

                        this.authData.access_token = token.access_token;
                        this.authData = { ...this.authData };

                        this.refreshTokenSubject.next(token.access_token);

                        return next.handle(this.addTokenHeader(request, token.access_token));
                    }),
                    catchError((err: HttpErrorResponse) => {
                        this.isRefreshing = false;

						if (err?.status == 401) this.authService.logout();
						
                        return throwError(() => err);
                    })
                );
            }
        }

        return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap((token) => next.handle(this.addTokenHeader(request, token)))
        );
    }

    private addTokenHeader(request: HttpRequest<unknown>, token: string, type: string = 'Bearer'): HttpRequest<unknown> {
        return request.clone({ setHeaders: { Authorization: type + ' ' + token } });
    }

	private isAuthUrl(url: string): boolean {
		if (url?.search(ENDPOINTS.LOGIN) != -1) return true;

		if (url?.search(ENDPOINTS.REFRESH) != -1) return true;

		return false;
	}

	private setRequiredHeaders(request: HttpRequest<unknown>): HttpRequest<unknown> {
        let headerObject: { [name: string]: string | string[] } = {
            fOrigin: 'webadmin',
        };

        return request.clone({ setHeaders: headerObject});
    }
}

export const AuthInterceptorProviders = [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];